import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { POSOrder, POSOrderItem, POSOrderWithItems } from '../types/admin';

// Get active orders for a table
export function useTableOrder(tableId?: string) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['pos-table-order', tableId],
    queryFn: async (): Promise<POSOrderWithItems | null> => {
      if (!company?.id || !tableId) return null;

      // Get active order for this table (including ready orders waiting to be paid)
      const { data: order, error: orderError } = await supabase
        .from('pos_orders')
        .select('*')
        .eq('table_id', tableId)
        .in('status', ['pending', 'confirmed', 'preparing', 'ready'])
        .single();

      if (orderError) {
        // PGRST116 means no rows - table has no active order
        if (orderError.code === 'PGRST116') return null;
        throw orderError;
      }

      // Get order items
      const { data: items, error: itemsError } = await supabase
        .from('pos_order_items')
        .select('*')
        .eq('order_id', order.id)
        .order('created_at', { ascending: true });

      if (itemsError) throw itemsError;

      return {
        ...order,
        items: items || [],
      } as POSOrderWithItems;
    },
    enabled: !!company?.id && !!tableId,
  });
}

// Get all active orders
export function useActiveOrders() {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['pos-active-orders', company?.id],
    queryFn: async (): Promise<POSOrderWithItems[]> => {
      if (!company?.id) return [];

      const { data: orders, error: ordersError } = await supabase
        .from('pos_orders')
        .select('*')
        .eq('company_id', company.id)
        .in('status', ['pending', 'confirmed', 'preparing', 'ready'])
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      if (!orders || orders.length === 0) return [];

      // Get items for all orders
      const orderIds = orders.map(o => o.id);
      const { data: items, error: itemsError } = await supabase
        .from('pos_order_items')
        .select('*')
        .in('order_id', orderIds);

      if (itemsError) throw itemsError;

      // Group items by order
      return orders.map(order => ({
        ...order,
        items: (items || []).filter(item => item.order_id === order.id),
      })) as POSOrderWithItems[];
    },
    enabled: !!company?.id,
  });
}

// Create new order
export function useCreateOrder() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      order_type: 'dine-in' | 'takeaway' | 'delivery';
      table_id?: string;
      area_id?: string;
      customer_name?: string;
      customer_phone?: string;
    }) => {
      if (!company?.id) throw new Error('No company selected');

      // Generate order number
      const orderNumber = `ORD-${Date.now()}`;

      const { data: order, error } = await supabase
        .from('pos_orders')
        .insert({
          company_id: company.id,
          order_number: orderNumber,
          order_type: data.order_type,
          table_id: data.table_id,
          area_id: data.area_id,
          customer_name: data.customer_name,
          customer_phone: data.customer_phone,
          status: 'pending',
          subtotal: 0,
          vat_amount: 0,
          discount_amount: 0,
          total_amount: 0,
        })
        .select()
        .single();

      if (error) throw error;
      return order as POSOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-table-order'] });
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}

// Replace all order items (for editing)
export function useReplaceOrderItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      order_id: string;
      items: Array<{
        product_id: string;
        product_code: string;
        product_name: string;
        quantity: number;
        unit_price: number;
        vat_rate: number;
        discount_rate?: number;
        notes?: string;
      }>;
    }) => {
      // Delete all existing items
      await supabase
        .from('pos_order_items')
        .delete()
        .eq('order_id', data.order_id);

      // Insert new items
      const itemsToInsert = data.items.map(item => {
        const quantity = item.quantity;
        const unitPrice = item.unit_price;
        const vatRate = item.vat_rate;
        const discountRate = item.discount_rate || 0;

        // Calculate amounts
        const subtotal = quantity * unitPrice;
        const discountAmount = subtotal * (discountRate / 100);
        const subtotalAfterDiscount = subtotal - discountAmount;
        const vatAmount = subtotalAfterDiscount * (vatRate / 100);
        const total = subtotalAfterDiscount + vatAmount;

        return {
          order_id: data.order_id,
          product_id: item.product_id,
          product_code: item.product_code,
          product_name: item.product_name,
          quantity,
          unit_price: unitPrice,
          vat_rate: vatRate,
          discount_rate: discountRate,
          subtotal,
          vat_amount: vatAmount,
          discount_amount: discountAmount,
          total_amount: total,
          status: 'pending' as const,
          notes: item.notes,
        };
      });

      if (itemsToInsert.length > 0) {
        const { error } = await supabase
          .from('pos_order_items')
          .insert(itemsToInsert);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-table-order'] });
    },
  });
}

// Add item to order
export function useAddOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      order_id: string;
      product_id: string;
      product_code: string;
      product_name: string;
      quantity: number;
      unit_price: number;
      vat_rate: number;
      discount_rate?: number;
      notes?: string;
    }) => {
      const quantity = data.quantity;
      const unitPrice = data.unit_price;
      const vatRate = data.vat_rate;
      const discountRate = data.discount_rate || 0;

      // Calculate amounts
      const subtotal = quantity * unitPrice;
      const discountAmount = subtotal * (discountRate / 100);
      const subtotalAfterDiscount = subtotal - discountAmount;
      const vatAmount = subtotalAfterDiscount * (vatRate / 100);
      const total = subtotalAfterDiscount + vatAmount;

      const { data: item, error } = await supabase
        .from('pos_order_items')
        .insert({
          order_id: data.order_id,
          product_id: data.product_id,
          product_code: data.product_code,
          product_name: data.product_name,
          quantity,
          unit_price: unitPrice,
          vat_rate: vatRate,
          discount_rate: discountRate,
          subtotal,
          vat_amount: vatAmount,
          discount_amount: discountAmount,
          total_amount: total,
          status: 'pending',
          notes: data.notes,
        })
        .select()
        .single();

      if (error) throw error;
      return item as POSOrderItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-table-order'] });
    },
  });
}

// Update order status
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      order_id: string;
      status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled';
    }) => {
      const updateData: any = { status: data.status };

      if (data.status === 'completed') {
        updateData.completed_at = new Date().toISOString();
      } else if (data.status === 'cancelled') {
        updateData.cancelled_at = new Date().toISOString();
      }

      const { data: order, error } = await supabase
        .from('pos_orders')
        .update(updateData)
        .eq('id', data.order_id)
        .select()
        .single();

      if (error) throw error;
      return order as POSOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-table-order'] });
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}

// Delete order item
export function useDeleteOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase
        .from('pos_order_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-table-order'] });
    },
  });
}

// Delete entire order
export function useDeleteOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from('pos_orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-table-order'] });
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}
